// pages/login.js
import { useState } from 'react';
import { createClient } from '@/lib/supabase';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Head from 'next/head';
import { XCircle } from 'lucide-react';

// Error message component
const ErrorMessage = ({ message }) => (
    <div className="mb-4 p-3 flex gap-4 bg-red-100 border-l-4 rounded-sm border-red-500 text-red-700">
        <XCircle className="h-5 w-5 text-red-400" />
        <p className="text-sm">{message}</p>
    </div>
);

const SuccessMessage = ({ message }) => (
    <div className="mb-4 p-3 flex gap-4 bg-green-100 border-l-4 rounded-sm border-green-500 text-green-700">
        <span className="font-semibold text-green-700">✓</span>
        <p className="text-sm">{message}</p>
    </div>
);

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const router = useRouter();
    const supabase = createClient();

    const handleLogin = async (e) => {
        e.preventDefault();
        setError(null);
        setInfo(null);
        setLoading(true);

        try {
            const { data, error: signInError } = await supabase.auth.signInWithPassword({
                email: email.trim().toLowerCase(),
                password,
            });

            if (signInError) {
                if (signInError.message === 'Email not confirmed') {
                    try {
                        const { error: resendError } = await supabase.auth.resend({
                            type: 'signup',
                            email: email.trim().toLowerCase(),
                        });
                        if (resendError) {
                            throw resendError;
                        }
                        setInfo('Please confirm your email. We just sent you a new verification link.');
                    } catch (resendErr) {
                        setError(resendErr?.message || 'Please confirm your email to continue.');
                    }
                    setPassword('');
                    return;
                }
                setError(getErrorMessage(signInError));
                setPassword('');
                return;
            }

            if (data?.user) {
                // Get redirect URL from query params or default to dashboard
                const params = new URLSearchParams(window.location.search);
                const redirectTo = params.get('redirectTo') || '/dashboard';
                window.location.replace(redirectTo);
            }
        } catch (error) {
            console.log('Login error:', error);
            setError(getErrorMessage(error));
            setPassword('');
        } finally {
            setLoading(false);
        }
    };

    const getErrorMessage = (error) => {
        if (error.message === 'Invalid login credentials') {
            return 'The email or password you entered is incorrect.';
        }
        if (error.message === 'Email not confirmed') {
            return 'Please verify your email address before logging in.';
        }
        // return 'An error occurred. Please try again.';
        return error.message;
    };

    return (
        <div className="relative p-8 max-sm:p-4 flex min-h-screen items-center justify-center w-full bg-wave">
            <Head>
                <title>Login | ISABHEL</title>
            </Head>
            <div className="w-full max-w-lg px-20 py-10 max-sm:p-4 space-y-6 bg-gray-400/20 backdrop-blur-lg rounded-lg shadow-md"
            >
                <h2 className="text-2xl font-bold text-center text-white text-shadow-md">
                    Welcome Back
                </h2>
                {info && <SuccessMessage message={info} />}
                {error && <ErrorMessage message={error} />}
                <form onSubmit={handleLogin} className="space-y-6">
                    {/* Email Input */}
                    <div>
                        <label htmlFor="email"
                            className="block text-sm font-bold text-white text-shadow-md mb-2"
                        >
                            Email
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            disabled={loading}
                            className="w-full px-4 py-2 bg-teal-700 
                            inset-shadow-sm inset-shadow-teal-500 rounded-lg 
                            text-white placeholder:text-white
                            focus:outline-hidden focus:ring-2 focus:ring-teal-200
                            disabled:opacity-50 disabled:bg-gray-100"
                            placeholder="Enter your email"
                        />
                    </div>

                    {/* Password Input */}
                    <div>
                        <label htmlFor="password" className="block text-sm font-bold text-white text-shadow-md mb-2">Password</label>
                        <input
                            type="password"
                            id="password"
                            name="current-password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            disabled={loading}
                            className="w-full px-4 py-2 bg-teal-700 
                            inset-shadow-sm inset-shadow-teal-500 rounded-lg 
                            text-white placeholder:text-white 
                            focus:outline-hidden focus:ring-2 focus:ring-teal-200 
                            disabled:opacity-50 disabled:bg-gray-100"
                            placeholder="Enter your password"
                        />
                    </div>

                    {/* Login Button */}
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full mt-2 py-3 text-white bg-teal-800 hover:bg-teal-900 inset-shadow-sm inset-shadow-teal-500 transition-all rounded-lg font-medium text-md disabled:opacity-50"
                    >
                        {loading ? 'Logging in...' : 'Log In'}
                    </button>

                </form>

                <p className="text-center text-gray-100">
                    Don&apos;t have an account?{' '}
                    <Link href="/signup" className="text-teal-50 hover:text-teal-200 font-bold">
                        Sign Up
                    </Link>
                </p>
                <div className="text-center">
                    <Link
                        href="/reset-password"
                        className="text-sm text-white underline decoration-teal-200 hover:text-teal-200"
                    >
                        Forgot password?
                    </Link>
                </div>
            </div>
        </div>
    );
}

Login.useLayout = false;
